import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { isClickedOutside } from '@theme/utilities';

/** The space kept between a focused control and the pinned checkout bar (--se2-space-8). */
const REVEAL_GAP = 8;

/**
 * Whether the element shows a keyboard focus indicator. A browser without :focus-visible throws on the selector;
 * there the pointer check in #revealAboveBar decides alone.
 * @param {Element} element
 * @returns {boolean}
 */
function isFocusVisible(element) {
  try {
    return element.matches(':focus-visible');
  } catch {
    return true;
  }
}

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, the installments CTA close-on-click, and light dismiss on desktop.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /**
   * The share of the drawer the pinned summary may take before it stops being sticky. SE2 · V-cart-08 (2026-10-06):
   * only the total and checkout are pinned now (snippets/cart-drawer.liquid), about 150px, 18% of a 390x844 phone;
   * past 30% (a phone on its side, a very short window) the bar scrolls with the cart instead of covering it.
   * Horizon used 0.5, which let the old whole-summary block cover 40% of a phone.
   * @type {number}
   */
  #summaryThreshold = 0.3;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  /**
   * Re-applies the sticky threshold when the panel changes size (a phone turned on its side, a resized window);
   * before V-cart-08's fix round it only ran on open and after cart updates.
   * @type {ResizeObserver | null}
   */
  #resizeObserver = null;

  /**
   * V-cart-08 (fix round 2): recomputes the sticky state when the bar itself arrives, leaves or is replaced. An empty
   * drawer that fills while open (a first add from a product page, the kit or the quiz, or the empty state's
   * suggestions) gets its summary from cart-items-component's morph, after the open and the settle had already
   * measured a drawer with no summary; the dialog keeps its height on a phone, so the ResizeObserver never ran again
   * and checkout stayed unpinned until the next cart update.
   * @type {MutationObserver | null}
   */
  #summaryObserver = null;

  /** @type {Element | null} */
  #observedSummary = null;

  #summaryCheckQueued = false;

  /**
   * True when the shopper's last input was a pointer press rather than a key (fix round 2). See #revealAboveBar.
   * @type {boolean}
   */
  #pointerInput = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.#dialog?.addEventListener('focusin', this.#onFocusIn);
    document.addEventListener('pointerdown', this.#onPointerDown, true);
    document.addEventListener('keydown', this.#onKeyDown, true);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#updateStickyState());
      const dialog = this.#dialog;
      if (dialog) this.#resizeObserver.observe(dialog);
    }

    const dialog = this.#dialog;
    if (dialog && 'MutationObserver' in window) {
      this.#observedSummary = dialog.querySelector('.cart-drawer__summary');
      this.#summaryObserver = new MutationObserver(this.#onDialogMutation);
      this.#summaryObserver.observe(dialog, { childList: true, subtree: true });
    }

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.#dialog?.removeEventListener('focusin', this.#onFocusIn);
    document.removeEventListener('pointerdown', this.#onPointerDown, true);
    document.removeEventListener('keydown', this.#onKeyDown, true);
    document.removeEventListener('click', this.#onOutsideClick);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#summaryObserver?.disconnect();
    this.#summaryObserver = null;
    this.#observedSummary = null;
  }

  /**
   * Coalesces the dialog's DOM changes to one check per frame and re-measures only when the bar element changed:
   * it appeared (empty to filled), left (filled to empty) or was replaced by a re-render. Size changes of the
   * current bar are the ResizeObserver's.
   */
  #onDialogMutation = () => {
    if (this.#summaryCheckQueued) return;
    this.#summaryCheckQueued = true;
    requestAnimationFrame(() => {
      this.#summaryCheckQueued = false;
      const summary = this.#dialog?.querySelector('.cart-drawer__summary') ?? null;
      if (summary === this.#observedSummary) return;
      this.#observedSummary = summary;
      this.#updateStickyState();
    });
  };

  #onPointerDown = () => {
    this.#pointerInput = true;
  };

  #onKeyDown = () => {
    this.#pointerInput = false;
  };

  /**
   * SE2 · V-cart-08 (fix round 1, WCAG 2.4.11 focus not obscured): Chrome does not scroll a focused control that is
   * already inside the scroller, even when the pinned checkout bar covers it, so the "Complete your room" add
   * buttons and, on a short phone, a line's stepper took focus out of sight. After the browser's own scroll this
   * moves the scroller just enough to show the control 8px above the bar. Only while the bar is pinned, and only
   * for what comes before it: the wallets and reassurance after it are never under it. The jump is instant, as a
   * browser's own focus scroll is. The scroller's scroll padding (snippets/cart-drawer) does the same for
   * scrollIntoView, which the line errors use.
   *
   * Keyboard focus only (fix round 2). Chrome focuses a button or link on mousedown, so for a pressed control
   * straddling the bar's top edge this scrolled the list between mousedown and mouseup: the content jumped under the
   * pointer, and a press within 8px of the bar ended on the bar, so no click fired and nothing was added. A control
   * the pointer pressed was already visible. Two checks, because each misses a case: :focus-visible is false for a
   * mouse or touch press on a button or link but true for the quantity field however it is focused, and the input
   * flag (the last pointerdown or keydown anywhere) covers that field. Tab, Shift+Tab and the focus that
   * component-cart-items gives back after a key press pass both.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (this.#pointerInput || !isFocusVisible(target)) return;
    requestAnimationFrame(() => this.#revealAboveBar(target));
  };

  /** @param {HTMLElement} element */
  #revealAboveBar(element) {
    const dialog = this.#dialog;
    if (!dialog || dialog.getAttribute('cart-summary-sticky') !== 'true' || !element.isConnected) return;
    if (document.activeElement !== element || this.#pointerInput || !isFocusVisible(element)) return;

    const scroller = element.closest('.cart-drawer__content');
    const bar = dialog.querySelector('.cart-drawer__summary');
    if (!(scroller instanceof HTMLElement) || !bar || !dialog.contains(scroller) || bar.contains(element)) return;
    if (bar.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING) return;

    // Only a control the bar actually covers moves. Once the bar has scrolled to its own place in the flow, the rail
    // ends right on its top edge; nudging that would only creep the scroller on every Tab.
    const rect = element.getBoundingClientRect();
    const barTop = bar.getBoundingClientRect().top;
    if (rect.height === 0 || rect.bottom <= barTop) return;
    scroller.scrollBy({ top: rect.bottom - barTop + REVEAL_GAP, behavior: 'instant' });
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Light dismiss. Below 990px the panel is a modal dialog and ThemeDrawer's own panel-click handler closes it
    // when the click lands on the ::backdrop. Above it the panel is opened with show(), which renders no
    // ::backdrop at all, so clicking the page did nothing at all (decision 2026-09-15: clicking outside closes the
    // cart at every width). The listener is on the document rather than on the SE2 scrim because that scrim is
    // pointer-events:none, so the page underneath stays hoverable while the drawer is open.
    document.addEventListener('click', this.#onOutsideClick);

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #handleDrawerClose = () => {
    document.removeEventListener('click', this.#onOutsideClick);
  };

  /**
   * Closes the non-modal (desktop) panel when the click lands anywhere outside it.
   *
   * @param {MouseEvent} event - The click event.
   */
  #onOutsideClick = (event) => {
    const dialog = this.#dialog;
    if (!dialog?.open || dialog.matches(':modal')) return;
    if (!(event.target instanceof Element)) return;

    // The header bag toggles the drawer itself (on:click="#cart-drawer/toggle"). Closing here as well would race
    // that handler and leave the drawer open, so its own clicks are left alone.
    if (event.target.closest('[data-testid="cart-drawer-trigger"], [on\\:click^="#cart-drawer"]')) return;

    // A dialog opened over the cart (quick add, a nested picker) sits outside this panel in the DOM; clicks inside
    // it are not page clicks.
    const clickedDialog = event.target.closest('dialog');
    if (clickedDialog && clickedDialog !== dialog) return;

    if (!isClickedOutside(event, dialog)) return;

    this.#themeDrawer?.close();
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      dialog.style.setProperty('--cart-drawer-bar-height', '0px');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    // A closed panel has no height; it is measured again when it opens.
    if (drawerHeight === 0) return;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    const sticky = ratio <= this.#summaryThreshold;
    dialog.setAttribute('cart-summary-sticky', sticky ? 'true' : 'false');
    // V-cart-08 (fix round 1): the pinned bar's height, for the scroller's scroll padding (snippets/cart-drawer).
    // A component runtime value, not a design token.
    dialog.style.setProperty('--cart-drawer-bar-height', sticky ? `${Math.ceil(summaryHeight)}px` : '0px');
    // The bar re-renders with the cart, so the observer follows the current one (observing it twice is a no-op).
    this.#resizeObserver?.observe(summary);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
